import Loader from 'components/Loader'
import { PageLoader } from 'components/Page/Page.Loader'
import TextBodySmall from 'components/TextBodySmall'
import PaymentRow from 'containers/Payment/Row'
import { createLogger } from 'logger-js'
import {
  SETTINGS_MODEL_KEY,
  SETTINGS_MODEL_PAYMENT_TYPE
} from 'models/daos/settings/constants'
import openErrorBannerAction from 'modules/banner/actions/open-error'
import { getTotalPriceCartSelector } from 'modules/cart/selectors/get-total'
import {
  getNameCurrencySelector,
  getValueCurrencySelector
} from 'modules/currencies/selectors'
import { createOrderAction } from 'modules/orders/actions/create'
import { isPlacingOrderSelector } from 'modules/orders/selectors/is-placing'
import { initPayPalService } from 'modules/payment/services/paypal'
import REDUX_OPTION from 'modules/redux/constants/option'
import getValueReduxUtil from 'modules/redux/utils/get-value'
import { updateSettingsAction } from 'modules/settings/actions'
import { STYLES_MARGIN } from 'modules/styles/constants/index.cjs'
import React from 'react'
import { defineMessages } from 'react-intl'
import { connect } from 'react-redux'
import { createSelector } from 'reselect'
import getPriceStringUtil from 'utils/string/get-price'
import WithIntl from 'components/WithIntl'

const logger = createLogger({
  label: 'PaymentPayPal'
})

const messages = defineMessages({
  securePayment: {
    id: 'PaymentPayPal.securePayment',
    defaultMessage: 'Secure payment provided by PayPal.com'
  }
})

const PAY_PAL_BUTTON_ID = 'paypal-button-container'

class PaymentPayPalComponent extends React.Component {
  constructor (props) {
    super(props)
    this.state = {
      isLoadingPayPal: true
    }
    this.handleClickRow = this.handleClickRow.bind(this)
    this.placeOrder = this.placeOrder.bind(this)
  }

  shouldComponentUpdate (nextProps, nextState) {
    return (
      (
        this.state.isLoadingPayPal && !nextState.isLoadingPayPal
      ) || (
        this.props.isSelected !== nextProps.isSelected
      )
    )
  }

  componentDidMount () {
    if (!this.props.isSelected) {
      return
    }
    this.initPayPal()
  }

  async initPayPal () {
    try {
      await initPayPalService()
    } catch (error) {
      logger.error('init payPal failed: ', error)
      openErrorBannerAction(error)
    }
    this.setState({
      isLoadingPayPal: false
    }, () => {
      this.createPayPalButton()
    })
  }

  componentDidUpdate (prevProps) {
    if (!prevProps.isSelected && this.props.isSelected && BROWSER) {
      this.initPayPal()
    }
  }

  createPayPalButton () {
    const placeOrder = this.placeOrder
    const {
      dispatch,
      totalPrice,
      usdCurrencyValue
    } = this.props
    window.paypal.Buttons({
      createOrder: function (data, actions) {
        return actions.order.create({
          purchase_units: [
            {
              amount: {
                value: getPriceStringUtil(totalPrice / usdCurrencyValue)
              }
            }
          ]
        })
      },
      onError: function (error) {
        dispatch(openErrorBannerAction(error))
      },
      onApprove: async function (data, actions) {
        try {
          await actions.order.capture()
          placeOrder(data.orderID)
        } catch (error) {
          dispatch(openErrorBannerAction(error))
        }
      }
    })
      .render(`#${PAY_PAL_BUTTON_ID}`)
  }

  placeOrder (orderID) {
    this.props.dispatch(createOrderAction({
      payPalOrderId: orderID
    }))
  }

  handleClickRow () {
    this.props.dispatch(updateSettingsAction(SETTINGS_MODEL_KEY.PAYMENT_TYPE,
      SETTINGS_MODEL_PAYMENT_TYPE.PAY_PAL,
      { [REDUX_OPTION.OMIT_EDITING]: true }
    ))
  }

  renderRow () {
    return (
      <PaymentRow
        isSelected={this.props.isSelected}
        onClick={this.handleClickRow}
      >
        <span>PayPal</span>
      </PaymentRow>
    )
  }

  renderPaymentIsProvidedByPayPal () {
    return (
      <div
        style={{
          margin: `${STYLES_MARGIN.SMALL} 0`
        }}
      >
        <TextBodySmall>
          {this.props.intl.formatMessage(messages.securePayment)}
        </TextBodySmall>
      </div>
    )
  }

  renderLoader () {
    return <PageLoader />
  }

  renderPlacingOrderLoader () {
    if (!this.props.isPlacingOrder) {
      return
    }
    return <Loader hasOverlay />
  }

  renderSelectedContent () {
    if (!this.props.isSelected) {
      return
    }

    if (this.state.isLoadingPayPal) {
      return this.renderLoader()
    }

    return (
      <>
        {this.renderPaymentIsProvidedByPayPal()}
        {this.renderPayPalButton()}
        {this.renderPlacingOrderLoader()}
      </>
    )
  }

  renderPayPalButton () {
    return (
      <div
        id={PAY_PAL_BUTTON_ID}
      />
    )
  }

  render () {
    return (
      <div
        style={{
          marginTop: STYLES_MARGIN.BIG
        }}
      >
        {this.renderRow()}
        {this.renderSelectedContent()}
      </div>
    )
  }
}

function getUSDCurrencyValue () {
  return createSelector(state => state && state.currencies.data.USD,
    (usdCurrency) => getValueReduxUtil(usdCurrency, 'value')
  )
}

const select = createSelector(getTotalPriceCartSelector(),
  isPlacingOrderSelector(),
  getNameCurrencySelector(),
  getValueCurrencySelector(),
  getUSDCurrencyValue,
  (totalPrice, isPlacingOrder, currency, usdCurrencyValue) => (
    {
      totalPrice,
      isPlacingOrder,
      currency,
      usdCurrencyValue
    }
  )
)

const PaymentPayPal = WithIntl(connect(select)(PaymentPayPalComponent))

export default PaymentPayPal
